/*
 * A label's pill and its key (design review "Label colours", Even Tone): one component for every
 * place a label is drawn, so a row, the rail, the relabel menu and the conversation's head never
 * disagree. The pill is a dot and a word; its weight says whether the lead is still in play
 * (filled), closed (outlined) or noise (bare, its dot a ring). A label the AI was unsure of has a
 * dashed edge, half its fill and a hollow dot: the "?" that read as a question beside a label called
 * Question is gone, and its pointer's tip and the screen-reader text still say the AI was not sure.
 */
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import { LABEL_KEYS, LABEL_NAMES, LABEL_WEIGHTS } from "./labels.ts";

/** What a label the AI was unsure of says to the pointer. */
const UNSURE_TIP = "The AI was not sure: check it";

/** The attributes that key a label's colour and weight; "No label" is a graphite ring. */
function labelKeyAttributes(code: InboxLabelCode | null, unsure = false) {
  return {
    "data-label-key": code === null ? "graphite" : LABEL_KEYS[code],
    "data-weight": code === null ? "noise" : LABEL_WEIGHTS[code],
    "data-unsure": unsure || undefined,
  };
}

export function LabelPill(props: {
  code: InboxLabelCode;
  uncertain?: boolean;
  className?: string;
}) {
  const unsure = props.uncertain === true;
  return (
    <span
      className={`lcr-label-pill${props.className ? ` ${props.className}` : ""}`}
      {...labelKeyAttributes(props.code, unsure)}
      title={unsure ? UNSURE_TIP : undefined}
    >
      <span className="lcr-label-dot" aria-hidden="true" />
      {LABEL_NAMES[props.code]}
      {unsure && <span className="lcr-visually-hidden"> (the AI was not sure)</span>}
    </span>
  );
}

/** A label's key alone, where the word is beside it: the rail, the relabel menu, the head. */
export function LabelDot(props: { code: InboxLabelCode | null; unsure?: boolean }) {
  return (
    <span
      className="lcr-label-dot"
      {...labelKeyAttributes(props.code, props.unsure === true)}
      aria-hidden="true"
    />
  );
}

const DATA_REQUEST_NOTE = "may need an answer within one month; the controller answers";

export function DataRequestMarker() {
  return (
    <span className="lcr-data-request" title={DATA_REQUEST_NOTE}>
      Data request<span className="lcr-visually-hidden">: {DATA_REQUEST_NOTE}</span>
    </span>
  );
}
